import { useAppConfig } from "~/app/config/useAppConfig";
import { memo, type ReactNode } from "react";

import { type PriceData } from "~/domains/pricing/types/PriceData";
import { type WeightData } from "~/domains/product/types/WeightData";
import * as PriceUtils from "~/utils/PriceUtils";
import * as WeightUtils from "~/utils/WeightUtils";
import { Box, Divider as MuiDivider, Stack, styled, Typography } from "~/common/mui";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useTranslation } from "react-i18next";
import { C_ON_SUCCESS_CONTAINER, C_ON_SURFACE_LOW_EMPHASIS } from "~/common/components/icon/Colors";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";

// ============================================================================
// Types
// ============================================================================

interface RowProps {
  className?: string;
  "data-test-id"?: string;
  label: ReactNode;
  value: ReactNode;
}

interface WeightRowProps {
  className?: string;
  "data-test-id"?: string;
  weight?: WeightData;
}

interface SurchargeRowProps {
  className?: string;
  "data-test-id"?: string;
  surcharge?: PriceData;
}

interface DiscountRowProps {
  className?: string;
  "data-test-id"?: string;
  discount?: PriceData | null;
}

interface TotalRowProps {
  className?: string;
  "data-test-id"?: string;
  value: PriceData | null;
  showPriceOnRequest?: boolean;
  subtitle?: ReactNode;
  asterisk?: boolean;
}

interface TaxDisclaimerProps {
  className?: string;
  "data-test-id"?: string;
  message?: ReactNode;
}

// ============================================================================
// Styled Components
// ============================================================================

const RowContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
});

const Divider = styled(MuiDivider)(({ theme }) => ({
  marginBlock: theme.spacing(4),
}));

const Actions = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
}));

const DataContainer = styled(Stack)({
  gap: 8,
});

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Generic row with label and value
 */
const Row = ({ label, value, className, "data-test-id": testId }: RowProps) => (
  <RowContainer className={className} data-test-id={testId}>
    <Typography>{label}</Typography>
    <Typography>{value}</Typography>
  </RowContainer>
);

/**
 * Weight row with formatted weight value. Returns null if weight is undefined.
 */
const WeightRow = ({ weight, className, "data-test-id": testId }: WeightRowProps) => {
  const { t } = useTranslation();
  if (!weight) {
    return null;
  }
  return (
    <Row
      className={className}
      data-test-id={testId ?? "total-weight"}
      label={t("cartPage.totalWeight")}
      value={WeightUtils.text(weight)}
    />
  );
};

/**
 * Surcharge row with formatted price. Returns null if surcharge is undefined.
 */
const SurchargeRow = ({ surcharge, className, "data-test-id": testId }: SurchargeRowProps) => {
  const { t } = useTranslation();
  if (!surcharge) {
    return null;
  }
  return (
    <Row
      className={className}
      data-test-id={testId ?? "total-surcharge"}
      label={t("page.cart.totals.surcharge")}
      value={PriceUtils.text(surcharge)}
    />
  );
};

/**
 * Discount row with SaleTag icon and green text. Returns null if discount is undefined or <= 0.
 */
const DiscountRow = ({ discount, className, "data-test-id": testId }: DiscountRowProps) => {
  const { t } = useTranslation();
  if (!discount || discount.value <= 0) {
    return null;
  }
  return (
    <RowContainer className={className} data-test-id={testId ?? "total-discount"}>
      <Stack direction="row" gap={2}>
        <Typography variant="bodyLarge">{t("checkoutPage.totalDiscount")}</Typography>
        <SaleTag size={24} />
      </Stack>
      <Typography color={C_ON_SUCCESS_CONTAINER} component="b">
        {PriceUtils.text2(discount, "")}
      </Typography>
    </RowContainer>
  );
};

/**
 * Total row with bold styling and optional price on request
 */
const TotalRow = ({
  value,
  showPriceOnRequest,
  subtitle,
  asterisk,
  className,
  "data-test-id": testId,
}: TotalRowProps) => {
  const { t } = useTranslation();

  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const message =
    showPriceOnRequest && !userHasPermission(Permission.ADD_TO_QUOTE) && !webservicesDisabled
      ? t("product.priceOnRequest")
      : "-";

  const displayValue = value ? PriceUtils.text2(value, message) : "...";
  const displayWithAsterisk = value && asterisk ? `${displayValue}*` : displayValue;

  return (
    <RowContainer className={className} data-test-id={testId ?? "total-price"}>
      <div>
        <Typography component="b" variant="titleLarge">
          {t("checkoutPage.totalPrice")}
        </Typography>
        {subtitle && <div>{subtitle}</div>}
      </div>
      <Typography component="b" variant="titleLarge">
        {displayWithAsterisk}
      </Typography>
    </RowContainer>
  );
};

/**
 * Tax disclaimer text
 */
const TaxDisclaimer = ({ message, className, "data-test-id": testId }: TaxDisclaimerProps) => {
  const { t } = useTranslation();
  return (
    <Typography className={className} data-test-id={testId} color={C_ON_SURFACE_LOW_EMPHASIS}>
      {message ?? t("checkoutPage.total.taxes")}
    </Typography>
  );
};

// ============================================================================
// Root Component
// ============================================================================

/**
 * OrderSummary compound component for displaying order totals.
 *
 * @example
 * ```tsx
 * <OrderSummary>
 *   <OrderSummary.WeightRow weight={totalWeight} />
 *   <OrderSummary.TotalRow value={totalSum} />
 *   <OrderSummary.TaxDisclaimer />
 *   <OrderSummary.Divider />
 *   <OrderSummary.Actions>
 *     <Button>Check out</Button>
 *   </OrderSummary.Actions>
 * </OrderSummary>
 * ```
 */
const OrderSummaryRoot = styled(Box)({
  width: 480,
});
// ============================================================================
// Compound Component Assembly
// ============================================================================

type OrderSummaryComponent = typeof OrderSummaryRoot & {
  Row: typeof Row;
  WeightRow: typeof WeightRow;
  SurchargeRow: typeof SurchargeRow;
  DiscountRow: typeof DiscountRow;
  TotalRow: typeof TotalRow;
  TaxDisclaimer: typeof TaxDisclaimer;
  Divider: typeof Divider;
  Actions: typeof Actions;
  DataContainer: typeof DataContainer;
};

const MemoizedRoot = memo(OrderSummaryRoot) as unknown as OrderSummaryComponent;

MemoizedRoot.Row = Row;
MemoizedRoot.WeightRow = WeightRow;
MemoizedRoot.SurchargeRow = SurchargeRow;
MemoizedRoot.DiscountRow = DiscountRow;
MemoizedRoot.TotalRow = TotalRow;
MemoizedRoot.TaxDisclaimer = TaxDisclaimer;
MemoizedRoot.Divider = Divider;
MemoizedRoot.Actions = Actions;
MemoizedRoot.DataContainer = DataContainer;

export { MemoizedRoot as OrderSummary };
